/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from '@hookform/error-message';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useApi from '../hooks/useApi';
import useListenSize from '../hooks/useListenSize';
import getNextPayDate from '../helpers/getNextPayDate';
import { addNewUserDetails } from '../redux/actions';
import ErrorHandling from '../components/ErrorHandling/index';
import SubmitButton from '../components/SubmitButton';
import NewUserBackButton from '../components/NewUserBackButton';

function NewUserIncome() {
  const {
    register,
    setValue,
    formState: { errors },
  } = useForm();
  const { response, loading, error } = useApi();

  const history = useHistory();
  const dispatch = useDispatch();
  useListenSize();

  const store = useSelector(state => ({
    newUserDetails: state.personal?.newUserDetails,
    sessionId: state.technical?.shopifySessionId,
    orgId: state.technical?.shopifyOrgId,
  }));

  const [lastPayDayDate, setLastPayDayDate] = useState(null);
  const [payFrequency, setPayFrequency] = useState(null);
  const [perPeriod, setPerPeriod] = useState('year');
  const [directDeposit, setDirectDeposit] = useState('yes');
  const [incomeValue, setIncomeValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setValue('income', store.newUserDetails?.income);
    setValue('per_period', store.newUserDetails?.per_period || 'year');
    setValue('payment_frequency', store.newUserDetails?.payment_frequency || 'default');
    setValue('direct_deposit', store.newUserDetails?.direct_deposit);
    if (store.newUserDetails?.last_pay_date) {
      setLastPayDayDate(new Date(store.newUserDetails?.last_pay_date));
    }
    if (store.newUserDetails?.income) {
      setIncomeValue(`$${store.newUserDetails?.income}`);
    }
    if (store.newUserDetails?.payment_frequency) {
      setPayFrequency(store.newUserDetails?.payment_frequency);
    }
  }, []);

  useEffect(() => {
    window.threatmetrix?.profile('tmx.katapult.com', store?.orgId, store?.sessionId, 2003);
    console.log('loaded 2003');
  }, []);

  const onSubmit = async () => {
    const formattedLastPayDayDate = moment(lastPayDayDate).format('YYYY-MM-DD');
    dispatch(addNewUserDetails({ last_pay_date: formattedLastPayDayDate }));
    dispatch(addNewUserDetails({ next_pay_date: getNextPayDate(payFrequency, lastPayDayDate) }));
    dispatch(addNewUserDetails({ direct_deposit: directDeposit }));
    dispatch(addNewUserDetails({ payment_frequency: payFrequency }));
    dispatch(addNewUserDetails({ per_period: perPeriod }));
    const unmaskedIncome = incomeValue?.replace('$', '');
    dispatch(addNewUserDetails({ income: unmaskedIncome }));
    setIsSubmitting(true);
    if (!lastPayDayDate) {
      return;
    }
    if (perPeriod === 'year' && parseFloat(unmaskedIncome, 10) < 10000) {
      history.push('/verify-income');
    } else {
      history.push('/new-user-review');
    }
  };

  const onIncomeFocus = value => {
    if (!value.includes('$')) {
      setIncomeValue(`$${value}`);
    } else {
      setIncomeValue(value);
    }
  };

  const onPayFreqChange = e => {
    setPayFrequency(e.target.value);
  };

  const onClickSubmit = e => {
    e.preventDefault();
    onSubmit();
  };

  const paymentFrequency = register('payment_frequency', {
    required: 'This field is required',
  });

  return (
    <div className="k-content-wrapper">
      <ErrorHandling response={response} error={error} />
      <section className="k-form k-form-indent">
        <form
          onSubmit={e => {
            onClickSubmit(e);
          }}
          autoComplete="off"
        >
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="income">Income</label>
            <div className="k-return-user-info">
              <input
                type="text"
                name="income"
                id="income"
                data-testid="income"
                placeholder="$45000"
                value={incomeValue}
                className="k-new-user-income-field"
                {...register('income', {
                  required: 'This is required',
                  onChange: e => {
                    onIncomeFocus(e.target.value);
                  },
                })}
              />
              <select
                name="per_period"
                id="per_period"
                data-testid="per_period"
                className="k-new-user-income-paycheck"
                ref={paymentFrequency.ref}
                {...register('per_period', {
                  required: 'This is required',
                  onChange: e => {
                    setPerPeriod(e.target.value);
                  },
                })}
              >
                <option value="year">Per year</option>
                <option value="paycheck">Per paycheck</option>
              </select>
            </div>
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="income" render={({ message }) => message} />
            </div>
            {!incomeValue && isSubmitting && <div className="k-form-validation-message">This field is required</div>}
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="payment_frequency">How often do you get paid?</label>
            <select
              name="payment_frequency"
              id="payment_frequency"
              defaultValue="default"
              {...register('payment_frequency', {
                required: 'This is required',
                onChange: e => onPayFreqChange(e),
              })}
            >
              <option value="default" disabled>
                Select a pay frequency
              </option>
              <option value="weekly">Weekly</option>
              <option value="bi-weekly">Bi-Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="semi-monthly">Semi-Monthly</option>
            </select>
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="payment_frequency" render={({ message }) => message} />
            </div>
            {!payFrequency && isSubmitting && <div className="k-form-validation-message">This field is required</div>}
          </div>
          <div className="k-field animate__animated animate__fadeInDown">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="last_pay_date">When was your last pay day?</label>
            <DatePicker
              id="last_pay_date"
              name="last_pay_date"
              onChange={date => {
                setLastPayDayDate(date);
              }}
              placeholderText="12/01/2023"
              selected={lastPayDayDate}
              maxDate={moment().toDate()}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="last_pay_date" render={({ message }) => message} />
            </div>
            {!lastPayDayDate && isSubmitting && <div className="k-form-validation-message">This field is required</div>}
          </div>
          <div className="k-field animate__animated animate__fadeInDown k-income-direct">
            <div>
              <div className="synchrony-deposit-title">Do you receive direct deposit?</div>
              <div className="synchrony-deposit-container">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="direct_deposit" className="k-income-direct-deposit" value="yes">
                  <input
                    type="radio"
                    className="k-income-deposit-input"
                    name="direct_deposit"
                    data-testid="deposit_yes"
                    value="yes"
                    checked={directDeposit === 'yes'}
                    {...register('direct_deposit', {
                      required: 'This is required',
                    })}
                  />
                  <span className="k-income-deposit-text">Yes</span>
                </label>
                <label htmlFor="direct_deposit" className="k-income-direct-deposit" value="no">
                  <input
                    type="radio"
                    className="k-income-deposit-input"
                    name="direct_deposit"
                    data-testid="deposit_no"
                    value="no"
                    checked={directDeposit === 'no'}
                    {...register('direct_deposit', {
                      required: 'This is required',
                      onChange: e => {
                        setDirectDeposit(e.target.value);
                      },
                    })}
                  />
                  <span className="k-income-deposit-text">No</span>
                </label>
              </div>
            </div>
          </div>
          <SubmitButton loading={loading} />
          <NewUserBackButton />
        </form>
      </section>
    </div>
  );
}

export default NewUserIncome;
